import i18n from "i18next";

/**
 * Takes a date object or a string in DD.MM.YYYY format, and returns a locale
 * date string, using the provided language.
 * Days and months default to always be displayed as two digits.
 *
 * @param date - date object or string in DD-MM-YYYY format
 * @param language - a valid language string
 * @param options - options to override the default ones
 * @returns {string}
 */
export const getLocalizedDateString = (
  date: Date | string,
  language: string,
  options?: object,
): string => {
  const lang = language === "en" ? "en-GB" : language;

  return new Date(date).toLocaleDateString(lang, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...options,
  });
};

/**
 * Formats a year-month string ("YYYY.MM") into a localized
 * "Month YYYY" representation (e.g. "February 2026").
 *
 * The month in the input string is expected to be 1-based (01–12).
 * Internally it is converted to JavaScript's 0-based month index.
 */
export const formatYearMonth = (value?: string, locale?: string) => {
  if (!value) return "-";

  const [yearStr, monthStr] = value.split(".");
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;

  const date = new Date(year, month);
  if (isNaN(date.getTime())) return null;

  // use explicit locale → i18n resolved language → guarded localStorage → fallback
  const lang =
    locale ??
    i18n.resolvedLanguage ??
    (typeof window !== "undefined"
      ? (window.localStorage.getItem("i18nextLng") ?? undefined)
      : undefined) ??
    "de-DE";

  return new Intl.DateTimeFormat(lang, {
    month: "long",
    year: "numeric",
  }).format(date);
};
